import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../api/client';

interface HubInfo {
  name: string;
  port: number;
  endpoints: number;
  engines: string[];
  probe: { path: string; method: 'get' | 'options' };
}

const HUBS: HubInfo[] = [
  { name: 'Molecular Hub', port: 8090, endpoints: 15, engines: ['RDKit 2025.9', 'OpenBabel 3.1', 'PySCF', 'STOUT', 'OPSIN', 'PaDEL', 'AutoDock Vina'], probe: { path: '/v2/iupac/name', method: 'options' } },
  { name: 'Metabolism Hub', port: 8089, endpoints: 7, engines: ['RDKit', 'AutoDock Vina', 'Custom CYP rules'], probe: { path: '/v2/metabolism/liability', method: 'options' } },
  { name: 'Combi Hub', port: 8083, endpoints: 4, engines: ['RDKit', 'RDKit Depict'], probe: { path: '/v2/library/enumerate', method: 'options' } },
  { name: 'Kinetics Hub', port: 8091, endpoints: 6, engines: ['SciPy', 'NumPy'], probe: { path: '/v2/kinetics/fit/ic50', method: 'options' } },
  { name: 'Calcs Hub', port: 8095, endpoints: 3, engines: ['SymPy', 'Pint'], probe: { path: '/v2/calcs/equations', method: 'get' } },
  { name: 'PKPD Hub', port: 8084, endpoints: 3, engines: ['mrgsolve (R)', 'nlmixr2 (R)', 'PySB'], probe: { path: '/v2/pkpd/models', method: 'get' } },
  { name: 'Genomics Hub', port: 8087, endpoints: 13, engines: ['Minimap2', 'Flye', 'STAR', 'Salmon'], probe: { path: '/v2/genomics/pipelines', method: 'get' } },
  { name: 'GenBio Hub', port: 8093, endpoints: 3, engines: ['ESMFold', 'ProteinMPNN', 'ESM-1v'], probe: { path: '/v2/genbio/fold', method: 'options' } },
  { name: 'Antibody Hub', port: 8094, endpoints: 3, engines: ['ABodyBuilder2', 'Sapiens', 'BioPhi'], probe: { path: '/v2/antibody/humanize', method: 'options' } },
];

const TOTAL_TOOLS = 40;

const ENGINE_VERSIONS: { name: string; version: string; purpose: string }[] = [
  { name: 'RDKit', version: '2025.9.4', purpose: 'Cheminformatics, molecular descriptors, fingerprints' },
  { name: 'Indigo', version: '1.36.1', purpose: 'Molecule rendering, canonical SMILES, reactions' },
  { name: 'OpenBabel', version: '3.1', purpose: 'Format conversion (SMILES, InChI, SDF, MOL)' },
  { name: 'PySCF', version: '2.4', purpose: 'HOMO/LUMO orbital energy calculations' },
  { name: 'STOUT', version: '2.0', purpose: 'SMILES to IUPAC systematic naming' },
  { name: 'OPSIN', version: '2.8', purpose: 'Chemical name to structure parsing' },
  { name: 'AutoDock Vina', version: '1.2', purpose: 'Molecular docking, CYP active-site docking' },
  { name: 'SciPy', version: '1.14', purpose: 'Curve fitting: IC50, Michaelis-Menten, SPR' },
  { name: 'SymPy', version: '1.13', purpose: 'Symbolic equation solving (63 equations, 11 domains)' },
  { name: 'Pint', version: '0.24', purpose: 'Physical unit conversion and validation' },
  { name: 'mrgsolve', version: '1.5 (R)', purpose: 'Compartmental PK/PD simulation' },
  { name: 'nlmixr2', version: '3.0 (R)', purpose: 'Non-linear mixed-effects PK modelling' },
  { name: 'PySB', version: '1.16', purpose: 'Systems biology PK/PD models' },
  { name: 'PaDEL', version: '2.21', purpose: 'Extended molecular descriptor calculation' },
  { name: 'ESMFold', version: '2.0', purpose: 'Protein structure prediction from sequence (GPU)' },
  { name: 'ProteinMPNN', version: '1.0', purpose: 'Protein sequence design and optimization (GPU)' },
  { name: 'Sapiens', version: '1.0', purpose: 'Antibody humanization scoring (GPU)' },
];

const WorkbenchAbout: React.FC = () => {
  const [hubStatus, setHubStatus] = useState<Record<string, 'up' | 'down' | 'checking'>>({});
  const [latency, setLatency] = useState<Record<string, number>>({});

  useEffect(() => {
    const init: Record<string, 'up' | 'down' | 'checking'> = {};
    HUBS.forEach((h) => { init[h.name] = 'checking'; });
    setHubStatus(init);

    HUBS.forEach((hub) => {
      const start = Date.now();
      const req = hub.probe.method === 'get' ? apiClient.get(hub.probe.path) : apiClient.options(hub.probe.path);
      req
        .then(() => {
          setHubStatus((prev) => ({ ...prev, [hub.name]: 'up' }));
          setLatency((prev) => ({ ...prev, [hub.name]: Date.now() - start }));
        })
        .catch((err: any) => {
          // OPTIONS returns 405 but hub is still reachable; also 422 means hub responded
          const status = err?.response?.status;
          if (status && status < 500) {
            setHubStatus((prev) => ({ ...prev, [hub.name]: 'up' }));
            setLatency((prev) => ({ ...prev, [hub.name]: Date.now() - start }));
          } else {
            setHubStatus((prev) => ({ ...prev, [hub.name]: 'down' }));
          }
        });
    });

    setTimeout(() => {
      setHubStatus((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => { if (next[k] === 'checking') next[k] = 'down'; });
        return next;
      });
    }, 8000);
  }, []);

  const totalEndpoints = HUBS.reduce((s, h) => s + h.endpoints, 0);
  const hubsUp = Object.values(hubStatus).filter((s) => s === 'up').length;

  const statusDot = (s: 'up' | 'down' | 'checking') => (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6,
      backgroundColor: s === 'up' ? '#16a34a' : s === 'down' ? '#dc2626' : '#d97706',
      boxShadow: s === 'up' ? '0 0 6px rgba(22,163,106,0.5)' : undefined,
      animation: s === 'checking' ? 'pulse 1.5s infinite' : undefined,
    }} />
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 900, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
        <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
        <span style={{ color: 'var(--card-border)' }}>|</span>
        <span style={{ color: 'var(--text-primary)' }}>Technology Stack</span>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Technology Stack</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
          {HUBS.length} microservice hubs &middot; {totalEndpoints} API endpoints &middot; {TOTAL_TOOLS} workbench tools &middot; {ENGINE_VERSIONS.length} computational engines
        </p>
      </div>

      {/* Hub Status Grid */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Hub Status <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>({hubsUp}/{HUBS.length} online)</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 10 }}>
          {HUBS.map((hub) => (
            <div key={hub.name} style={{
              padding: '14px 16px', borderRadius: 10,
              background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {statusDot(hubStatus[hub.name] || 'checking')}
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{hub.name}</span>
                </div>
                <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-muted)' }}>:{hub.port}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                <span>{hub.endpoints} endpoints</span>
                {latency[hub.name] != null && (
                  <span style={{ fontFamily: 'monospace', color: latency[hub.name] < 200 ? '#16a34a' : '#d97706' }}>
                    {latency[hub.name]}ms
                  </span>
                )}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                {hub.engines.join(' \u00b7 ')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Engine Versions Table */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Computational Engines
        </h2>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, overflow: 'hidden' }}>
          {ENGINE_VERSIONS.map((eng, i) => (
            <div key={eng.name} style={{
              display: 'flex', alignItems: 'center', padding: '10px 16px', gap: 16,
              borderBottom: i < ENGINE_VERSIONS.length - 1 ? '1px solid var(--card-border)' : 'none',
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', minWidth: 120 }}>{eng.name}</span>
              <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#f59e0b', minWidth: 80 }}>{eng.version}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>{eng.purpose}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkbenchAbout;
